import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';

export interface RowMenuItem {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  /** Figma 699:3694 — "pen" for Edit role, "trash" for Remove member. */
  icon?: IconProp;
}

interface RowMenuProps {
  items: RowMenuItem[];
  triggerLabel: string;
  /**
   * Extra classes for the portaled menu list — it renders under
   * `document.body`, so page-scoped styles can't reach it via an ancestor.
   */
  menuClassName?: string;
}

interface MenuPosition {
  top: number;
  right: number;
}

/**
 * The "···" row-actions affordance (sc-43661 FR-008/FR-009). Whether it
 * renders at all for a given row — e.g. never on the caller's own row — is
 * the caller's decision, not this component's; it only renders its own
 * open/closed menu once mounted.
 *
 * The open menu is portaled to `document.body` with `fixed` coordinates
 * computed from the trigger's own rect, rather than `position: absolute`
 * inside the row. `TeamTable` wraps rows in an `overflow-x-auto` div for
 * horizontal scrolling, and setting only `overflow-x` makes the browser
 * compute `overflow-y` as `auto` too (CSS Overflow §3) — an `absolute`
 * dropdown anchored inside that div gets clipped whenever it opens on a row
 * near the bottom of the table, which is common for small rosters. Portaling
 * sidesteps that ancestor entirely.
 */
const RowMenu: React.FC<RowMenuProps> = ({ items, triggerLabel, menuClassName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const isInside = (target: Node) =>
      Boolean(containerRef.current?.contains(target)) || Boolean(menuRef.current?.contains(target));

    const handleClickOutside = (event: MouseEvent) => {
      if (!isInside(event.target as Node)) setIsOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    const handleScroll = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  return (
    <div className="au-row-menu" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`au-row-menu-trigger${isOpen ? ' au-row-menu-trigger--open' : ''}`}
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span aria-hidden="true">···</span>
      </button>
      {isOpen &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            className={`au-row-menu-list${menuClassName ? ` ${menuClassName}` : ''}`}
            role="menu"
            style={{ position: 'fixed', top: position.top, right: position.right }}
          >
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                className={`au-row-menu-item${item.variant === 'danger' ? ' au-row-menu-item--danger' : ''}`}
                onClick={() => {
                  setIsOpen(false);
                  item.onClick();
                }}
              >
                {/* FA 7 icons are fixed-width by default; no `fixedWidth` prop needed. */}
                {item.icon && (
                  <FontAwesomeIcon icon={item.icon} className="au-row-menu-item-icon" />
                )}
                {item.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default RowMenu;
